import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CungVeDichAPI } from '../services/API';

const OrderPreviewPublic = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orderData, setOrderData] = useState(null);

  useEffect(() => {
    const fetchOrderData = async () => {
      try {
        setLoading(true);
        setError('');
        console.log('Fetching public order data for id:', id);

        const result = await CungVeDichAPI.getOrderPublic(id);

        if (result.success && result.order) {
          console.log('Order data loaded:', result.order);
          console.log('📋 order.orderDetails:', result.order.orderDetails);
          console.log('👤 order.creator:', result.order.creator);

          // Transform order data to match preview format
          const order = result.order;
          const transformedData = {
            orderId: order.id,
            type: order.type, // Add order type (1=retail, 2=wholesale)
            customer: {
              name: order.customer?.name || order.customerName || 'N/A',
              facebook: order.customer?.facebook || '',
              phone: order.customer?.phone || order.customer?.phoneNumber || '',
              phoneNumber: order.customer?.phone || order.customer?.phoneNumber || ''
            },
            products: (order.orderDetails || []).map(detail => {
              const product = {
                id: detail.productId || detail.product?.id || detail.id,
                name: detail.product?.name || detail.productName || 'N/A',
                quantity: detail.quantity || 0,
                price: parseFloat(detail.unitPrice) || parseFloat(detail.product?.price) || 0,
                unitPrice: parseFloat(detail.unitPrice) || parseFloat(detail.product?.price) || 0,
                instruction: detail.product?.instruction || null
              };
              console.log('🛍️ Transformed product:', product);
              return product;
            }),
            totalAmount: parseFloat(order.totalAmount) || 0,
            createdBy: order.creator || order.createdBy || {},
            createdAt: order.createdAt,
            // Collect all instruction images and corresponding product names
            instructionImages: (() => {
              const map = new Map();
              (order.orderDetails || []).forEach(detail => {
                const url = detail.product?.instruction;
                if (url && typeof url === 'string' && url.startsWith('http')) {
                  if (!map.has(url)) map.set(url, new Set());
                  map.get(url).add(detail.product?.name || detail.productName || 'Sản phẩm');
                }
              });
              return Array.from(map.entries()).map(([url, namesSet]) => ({
                url,
                name: Array.from(namesSet).join(', ')
              }));
            })()
          };

          console.log('📦 Transformed order data:', transformedData);
          console.log('📦 Products count:', transformedData.products.length);
          console.log('🖼️ Instruction images:', transformedData.instructionImages);
          setOrderData(transformedData);
        } else {
          setError('Không tìm thấy đơn hàng');
        }
      } catch (err) {
        console.error('Error fetching order:', err);
        setError(err.message || 'Có lỗi xảy ra khi tải thông tin đơn hàng');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchOrderData();
    } else {
      setError('Không có ID đơn hàng');
      setLoading(false);
    }
  }, [id]);

  // Helper function for price formatting
  const formatPrice = (value) => {
    if (!value || value === '' || isNaN(parseFloat(value))) return '';
    return parseFloat(value).toLocaleString('vi-VN');
  };

  // Helper function for phone number formatting
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.toString().replace(/\D/g, '');
    if (cleaned.length < 4) return cleaned;
    // Format: 0123.456.789 (4 digits, then groups of 3)
    const first = cleaned.slice(0, 4);
    const rest = cleaned.slice(4);
    const groups = rest.match(/.{1,3}/g) || [];
    return [first, ...groups].join('.');
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Đang tải thông tin đơn hàng...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md">
          <div className="text-center">
            <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Lỗi</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Về trang chủ
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center">
        <p className="text-gray-600">Không có dữ liệu đơn hàng</p>
      </div>
    );
  }

  const { customer, products, totalAmount, orderId, createdBy } = orderData;

  return (
    <div className="fixed inset-0 bg-gray-100 z-50 flex flex-col">
      {/* Header with close button - compact */}
      <div className="bg-white px-3 py-2 flex justify-between items-center border-b shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900">
          📸 Đơn hàng #{orderId}
        </h3>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="text-gray-500 hover:text-gray-700"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Order Image/Card - scrollable content */}
      <div className="flex-1 overflow-y-auto p-2">
        <div className="bg-white rounded-lg shadow-lg p-3 border border-gray-200 max-w-2xl mx-auto">
          {/* Header - 2 columns layout */}
          <div className="grid grid-cols-2 gap-3 mb-3 pb-3 border-b border-gray-200">
            {/* Column 1: Order ID */}
            <div className="flex items-center">
              <h2 className="text-xl font-bold text-blue-600">
                ĐH: {orderId}
              </h2>
            </div>

            {/* Column 2: Customer Info */}
            <div className="flex flex-col justify-center">
              {customer && (
                <>
                  <div className="text-base font-bold text-gray-800 mb-1">
                    {orderData.type === 2 ? 'ĐL' : 'KH'}: {customer.facebook || customer.name}
                  </div>
                  <p className="text-xs text-gray-500">
                    {new Date(orderData.createdAt || Date.now()).toLocaleDateString('vi-VN', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </>
              )}
              {!customer && (
                <p className="text-xs text-gray-500">
                  {new Date(orderData.createdAt || Date.now()).toLocaleDateString('vi-VN', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              )}
            </div>
          </div>

          {/* Products - compact */}
          <div className="mb-3">
            <div className="bg-gray-50 rounded border border-gray-200 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-semibold text-gray-700">Sản phẩm</th>
                    <th className="px-1 py-1.5 text-center font-semibold text-gray-700">SL</th>
                    <th className="px-1 py-1.5 text-right font-semibold text-gray-700">Đơn giá</th>
                    <th className="px-2 py-1.5 text-right font-semibold text-gray-700">Thành tiền</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {products.map((product, index) => {
                    const quantity = parseInt(product.quantity) || 0;
                    const price = parseFloat(product.price || product.unitPrice) || 0;
                    const total = quantity * price;

                    return (
                      <tr key={product.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-2 py-1.5 font-medium text-gray-900">{product.name}</td>
                        <td className="px-1 py-1.5 text-center text-gray-700">{quantity.toLocaleString()}</td>
                        <td className="px-1 py-1.5 text-right text-gray-700">{formatPrice(price)}₫</td>
                        <td className="px-2 py-1.5 text-right font-semibold text-blue-600">{total.toLocaleString()}₫</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Total - compact */}
          <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded p-2 border border-orange-300">
            <div className="flex justify-between items-center">
              <span className="text-base font-bold text-gray-800">💰 Tổng cộng:</span>
              <span className="text-xs font-bold text-orange-600">
                {totalAmount.toLocaleString()}₫
              </span>
            </div>
          </div>
        </div>

        {/* Contact Info - compact */}
        {createdBy && (
          <div className="mt-2 p-3 bg-gradient-to-r from-sky-100 to-blue-100 rounded-lg border-2 border-sky-400 shadow-sm max-w-2xl mx-auto">
            <div className="flex items-center gap-3">
              {/* Avatar */}
              <div className="flex-shrink-0">
                {createdBy.avatar ? (
                  <img
                    src={createdBy.avatar}
                    alt="Avatar"
                    className="w-12 h-12 rounded-full border-2 border-sky-500 object-cover shadow-md"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-500 to-blue-500 border-2 border-sky-500 flex items-center justify-center shadow-md">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                )}
              </div>
              {/* Contact Text */}
              <div className="flex-1">
                <div className="flex items-center gap-1 mb-0.5">
                  <svg className="w-3.5 h-3.5 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <p className="text-xs text-gray-700">Khi cần hỗ trợ vui lòng liên hệ đại lý:</p>
                </div>
                <p className="text-sm leading-tight">
                  <span className="font-bold text-sky-600">{createdBy.facebook || createdBy.name}</span>
                  <span className="text-gray-400 mx-1">•</span>
                  <span className="font-semibold text-emerald-600">{formatPhoneNumber(createdBy.phoneNumber || createdBy.phone)}</span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Instruction Images */}
        {orderData.instructionImages && orderData.instructionImages.length > 0 && (
          <div className="mt-2 max-w-2xl mx-auto">
            {orderData.instructionImages.length === 1 ? (
              <div className="space-y-2">
                {orderData.instructionImages.map((item, index) => (
                  <div key={index}>
                    <img src={item.url} alt={item.name} className="w-full rounded border border-gray-200"
                      onError={(e) => { e.target.src = "https://cdn.cungvedich.vn/others/5c3379f9-152c-4cfa-8bc5-faa934fd45b1.jpg"; }} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Hướng dẫn sử dụng ({orderData.instructionImages.length} ảnh)
                </h4>
                <ul className="space-y-3">
                  {orderData.instructionImages.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 bg-white p-2 rounded border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                      <a href={item.url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 block">
                        <img src={item.url} alt={item.name} className="w-16 h-16 object-cover rounded border border-gray-100" />
                      </a>
                      <div className="flex-1 min-w-0 pt-1">
                        <a href={item.url} target="_blank" rel="noopener noreferrer"
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline block truncate" title={item.name}>
                          {item.name}
                        </a>
                        <p className="text-xs text-gray-500 mt-1">Click để xem ảnh lớn</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderPreviewPublic;

